import React, { useContext, useEffect, useMemo, useState } from 'react';
import AccountContext from '../../../../contexts/AccountContext';
import { CurrentUserContentSettingsContext } from '../CurrentUserContentSettingsProvider';
import CurrentUserContext from '../../../../contexts/CurrentUserContext';
import { PortalsContext, PortalsProviderContextType } from '../PortalsProvider';
import ImpersonatorContext from '../../../../contexts/ImpersonatorContext';
import useNavigateFleetOpsStaffToAdminPortal from './useNavigateFleetOpsStaffToAdminPortal';
import { useLocation } from 'react-router-dom';
import AccountPickerContext from '../../../../contexts/AccountPickerContext';
import FeatureGatesContext from '../../../../contexts/FeatureGatesContext';
import { UIVersion } from './types';

export const UIVersionContext = React.createContext<{
  uiVersion: UIVersion;
  isV3: boolean;
}>({ uiVersion: 'v1', isV3: false });

const useUrlParamVersion = () =>
  new URLSearchParams(useLocation().search).get('ui');

export const useQueryParamVersion = () => {
  const { selectedAccountId } = useContext(AccountPickerContext);
  const queryParam = useUrlParamVersion();
  const [queryParamVersion, setQueryParamVersion] = useState<
    'v1' | 'v2' | 'v3' | undefined
  >(() => {
    if (!queryParam) {
      return undefined;
    }

    if (queryParam === 'v1') {
      return 'v1';
    } else if (queryParam === 'v2') {
      return 'v2';
    } else if (queryParam === 'v3') {
      return 'v3';
    }

    return undefined;
  });

  useEffect(() => {
    return () => {
      setQueryParamVersion(undefined);
    };
  }, [selectedAccountId]);

  return queryParamVersion;
};

const UIVersionProvider = ({ children }: { children: React.ReactNode }) => {
  const { isWallboardUser, ui: actualCurrentUserUi } =
    useContext(CurrentUserContext);
  const { isV3UIEnabled } = useContext(AccountContext);
  const portalsContext = useContext(PortalsContext);
  const { contentSettings } = useContext(CurrentUserContentSettingsContext);
  const { viewingAppAs } = useContext(ImpersonatorContext);
  const queryParamVersion = useQueryParamVersion();

  const currentUserUi = useMemo(() => {
    if (viewingAppAs) {
      return viewingAppAs.ui;
    }

    return actualCurrentUserUi;
  }, [actualCurrentUserUi, viewingAppAs]);

  const uiVersion = useMemo((): UIVersion => {
    const {
      isPortalsEnabledAtAccountLevel,
      isPortalUIToggleEnabled,
      isPortalsEnabled,
    } = portalsContext;
    if (queryParamVersion) {
      return queryParamVersion;
    }

    if (isWallboardUser) {
      return 'wallboard';
    }

    if (isV3UIEnabled || currentUserUi === 'v3') {
      return 'v3';
    }

    if (isPortalsEnabledAtAccountLevel || currentUserUi === 'portals') {
      if (isPortalUIToggleEnabled) {
        if (!isPortalsEnabled) {
          return 'v1';
        }
      }

      if (contentSettings.mode === 'engagement') {
        return 'v2 - simplified';
      }

      return 'v2';
    }

    return 'v1';
  }, [
    portalsContext,
    queryParamVersion,
    isWallboardUser,
    isV3UIEnabled,
    currentUserUi,
    contentSettings.mode,
  ]);

  const isV3 = useMemo(() => {
    return uiVersion === 'v3';
  }, [uiVersion]);

  // This is a bad pattern, but I'm busy
  // Many screens depend on this for entering the V2 mode.
  // Really - those need to be updated to refer to the UIVersionContext
  const portalsContextOverride = useMemo((): PortalsProviderContextType => {
    if (isV3) {
      return {
        ...portalsContext,
        selectedPortal: undefined,
        isPortalsEnabled: false,
        isPortalsEnabledAtAccountLevel: false,
        isPortalUIToggleEnabled: false,
      };
    }

    return portalsContext;
  }, [isV3, portalsContext]);

  useNavigateFleetOpsStaffToAdminPortal(uiVersion);

  return (
    <PortalsContext.Provider value={portalsContextOverride}>
      <UIVersionContext.Provider value={{ uiVersion, isV3 }}>
        {children}
      </UIVersionContext.Provider>
    </PortalsContext.Provider>
  );
};

// Whether Edit and Create open Studio: where the account's studio gate is on
// (staff included only there), in a UI with Studio's routes: V1, V2 and V3.
// V2's engagement mode (only orders and entities) and wallboards don't.
// Otherwise everything uses the classic edit flow.
export const useCanOpenStudio = () => {
  const { uiVersion } = useContext(UIVersionContext);
  const { isStudioEnabled } = useContext(FeatureGatesContext);
  return (
    isStudioEnabled &&
    (uiVersion === 'v1' || uiVersion === 'v2' || uiVersion === 'v3')
  );
};

export default UIVersionProvider;
